import React, { useState, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Dropdown } from 'antd';
import { SearchInput } from "../../../components/form/SearchInput";

const JobBoardFilter2 = (props) => {
    const {
        value = [],
        options = [],
        label,
        subLabel,
        className,
        isEditing,
        isDisabled,
        searchEnabled,
        setImportanceLevel,
        setExperienceLevel,
        formErrors,
        right,
        renderLabel,
        onChange,
        onToggleEditing,
        onChangeOptions,
    } = props;

    const [search, setSearch] = useState('');
    const [suggested, setSuggested] = useState([]);
    const [editingSelected, setEditingSelected] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchError, setSearchError] = useState(false);
    const loadTimeoutRef = useRef(null);
    const containerRef = useRef(null);

    const selectedValues = useMemo(() => TB.keyBy(value || [], 'value'), [value]);

    const getOptions = useCallback(() => {
        if (suggested?.length) {
            return suggested;
        }
        return options;
    }, [suggested, options]);

    const handleChange = useCallback((option) => {
        let newValue;
        if (TB.isArray(value)) {
            if (setImportanceLevel && !option.importance) {
                option = { ...option, importance: 2 };
            }
            newValue = [...value];
            newValue = TB.xorBy(newValue, [option], 'value');
            if (!newValue.length) {
                setEditingSelected(false);
            }
        } else if (TB.isObject(value)) {
            newValue = { ...value };
            newValue[option.name] = !newValue[option.name];
        }
        return onChange(newValue);
    }, [value, setImportanceLevel, onChange]);

    const handleChangeImportance = useCallback((option, importance) => {
        if (TB.isArray(value)) {
            let existing = TB.findIndex(value, ['value', option.value]);

            if (existing > -1) {
                let newValue = TB.set([...value], `[${existing}].importance`, importance);
                return onChange(newValue);
            } else if (TB.isArray(suggested)) {
                let existingIdx = TB.findIndex(suggested, ['value', option.value]);
                if (existingIdx > -1) {
                    let selected = TB.set([...suggested], `[${existingIdx}].importance`, importance);
                    setSuggested(selected);
                    return;
                }
            }

            if (TB.isArray(options)) {
                let existingIdx = TB.findIndex(options, ['value', option.value]);
                if (existingIdx > -1) {
                    let newOptions = TB.set([...options], `[${existingIdx}].importance`, importance);
                    return onChangeOptions(newOptions);
                }
            }
        }
    }, [value, suggested, options, onChange, onChangeOptions]);

    const renderImportanceDropdown = (option, index, importance) => {
        if (!setImportanceLevel) return null;
        const isDisabledImportance = !value?.length || !TB.find(value, ['value', option.value]);
        const importanceItems = TB.map(laravel.importance_levels, (lvlLabel, level) => ({
            key: `${index}-importance-${level}`,
            label: lvlLabel,
            onClick: () => handleChangeImportance(option, level),
        }));
        return (
            <div className="filter-importance">
                <Dropdown
                    menu={{ items: importanceItems }}
                    trigger={['click']}
                    disabled={isDisabledImportance}
                >
                    <div className={TB.mapClass("importance-btn", { disabled: isDisabledImportance })}>
                        {laravel.importance_levels[importance]} <span className="anticon anticon-down" />
                    </div>
                </Dropdown>
            </div>
        );
    };

    const renderExperienceDropdown = (option, index, importance) => {
        if (!setExperienceLevel) return null;
        const experienceItems = TB.map(laravel.talent_experience_levels, (config, key) => ({
            key: `${index}-experience-${key}`,
            label: `${config.level} (${config.label})`,
            onClick: () => handleChangeImportance(option, key),
        }));
        return (
            <div className="filter-importance">
                <Dropdown
                    menu={{ items: experienceItems }}
                    trigger={['click']}
                >
                    <div className="importance-btn">
                        {laravel.talent_experience_levels[importance]?.level} <span className="anticon anticon-down" />
                    </div>
                </Dropdown>
            </div>
        );
    };

    const renderOption = (option, index, importance = undefined) => {
        let checked = TB.isArray(value)
            ? TB.map(value, 'value').includes(option.value)
            : !!value[option.name];

        if (importance === undefined) {
            if (setImportanceLevel) {
                importance = 2;
            } else if (setExperienceLevel) {
                importance = TB.values(laravel.talent_experience_levels)[0];
            }
        }

        return (
            <div
                key={`option-${TB.slugify(option.name || option.value)}-${index}-${importance}`}
                className={TB.mapClass("filter-option-form", { checked })}
            >
                <div className={TB.mapClass("form-checkbox", { "disabled-checkbox": option.disabled })}>
                    <input
                        type="checkbox"
                        className="form-check-input"
                        name={TB.isArray(value) ? `${TB.slugify(label)}[]` : TB.slugify(option.name || option.value)}
                        id={"filter" + option.name}
                        required={false}
                        disabled={option.disabled}
                        onChange={() => handleChange(option)}
                        checked={checked}
                    />
                    <label className="form-check-label" htmlFor={"filter" + option.name}>
                        {option.name}
                    </label>
                </div>
                {renderImportanceDropdown(option, index, importance)}
                {renderExperienceDropdown(option, index, importance)}
            </div>
        );
    };

    const dropdownOptions = editingSelected ? value : getOptions() || [];
    let allOptions = dropdownOptions;
    if (TB.findIndex(dropdownOptions, 'options') !== -1) {
        allOptions = TB.filter(TB.flatten(TB.map(dropdownOptions, 'options')), ['disabled', false]);
    }

    const menuContent = (
        <div className="filter-dropdown-menu bg-white" style={{ overflow: 'auto', maxHeight: '400px' }}>
            {/* Header */}
            <div className="ant-dropdown-menu-item ant-dropdown-menu-item-group-title">
                <small className="text-bold flex-row flex-no-wrap flex w-full">
                    <span className="text-bold mr-auto">
                        {value.length || 'None'} Selected
                    </span>
                    {value?.length > 0 && editingSelected && (
                        <span
                            className="ml-auto text-dark mr-1 clickable px-2 text-uppercase"
                            onClick={() => setEditingSelected(false)}
                        >
                            Back
                        </span>
                    )}
                    {value?.length > 0 && !editingSelected && (
                        <span
                            className="ml-auto text-warning mr-1 clickable px-2 text-uppercase"
                            onClick={() => {
                                setSearch('');
                                setSuggested([]);
                                setEditingSelected(true);
                            }}
                        >
                            Edit
                        </span>
                    )}
                    <span
                        className="ml-2 text-dark clickable px-2 text-uppercase"
                        onClick={onToggleEditing}
                    >
                        Done
                    </span>
                </small>
            </div>

            <div className="ant-dropdown-menu-item-divider" />

            {/* All/None toggle */}
            {!editingSelected && !TB.find(dropdownOptions, 'options') && (
                <div className="all-filter filter-option-form">
                    <div className="toggle-title">
                        {subLabel || TB.pluralize(label)}
                    </div>
                    <div className="toggle-select clickable" onClick={() => onChange(allOptions)}>
                        All
                    </div>
                    <div className="toggle-select clickable" onClick={() => onChange([])}>
                        None
                    </div>
                </div>
            )}

            {/* Search */}
            {searchEnabled && !editingSelected && (
                <div className="filter-search-input" key={TB.slugify(label) + '-search'}>
                    <SearchInput
                        value={search || ''}
                        placeholder="Start typing to search.."
                        onSearch={s => {
                            if (s === '') {
                                setSearch(s);
                                setSuggested([]);
                                setEditingSelected(false);
                            } else {
                                setSearch(s);
                                setEditingSelected(false);
                            }
                        }}
                        searchType={'skills'}
                        valueLabelDisplay={'name'}
                        onSuggestionsFetched={(fetchedSuggested) => {
                            fetchedSuggested = TB.map(fetchedSuggested, option => ({ ...option, value: option.id }));
                            setSuggested(fetchedSuggested);
                            setSearchError(false);
                        }}
                        onLoading={(loading) => {
                            clearTimeout(loadTimeoutRef.current);
                            if (loading) {
                                setSearchLoading(true);
                                setSearchError(false);
                                return;
                            }
                            setSearchError(false);
                            loadTimeoutRef.current = setTimeout(() => {
                                setSearchLoading(loading);
                            }, 250);
                        }}
                        onError={(err) => {
                            clearTimeout(loadTimeoutRef.current);
                            setSearchError(err);
                            setSuggested([...value]);
                            loadTimeoutRef.current = setTimeout(() => {
                                setSearchLoading(false);
                            }, 250);
                        }}
                        disabled={isDisabled}
                        name={TB.slugify(label) + '-search'}
                        searchRequestEndpoint={'/api/talent-skills/search'}
                    />
                </div>
            )}

            {/* Options */}
            {TB.map(editingSelected ? value : getOptions() || [], (option, index) => {
                if (option.options) {
                    return (
                        <React.Fragment key={TB.slugify(option.name) + '-group'}>
                            <div className="all-filter filter-option-form">
                                <div className="toggle-title">{option.name}</div>
                                <div className="toggle-select clickable" onClick={() => {
                                    let values = [...value, ...option.options];
                                    values = TB.uniqBy(values, 'value');
                                    onChange(values);
                                }}>
                                    All
                                </div>
                                <div className="toggle-select clickable" onClick={() => {
                                    let removeItemValues = TB.map(option.options, 'value');
                                    let values = TB.filter([...value], item => !removeItemValues.includes(item.value));
                                    onChange(values);
                                }}>
                                    None
                                </div>
                            </div>
                            {TB.map(option.options, (cOption, cIndex) => {
                                let importance;
                                let existing = selectedValues[cOption.value];
                                if (existing) {
                                    importance = existing.importance;
                                }
                                return renderOption(cOption, `${TB.slugify(option.name + cOption.name)}`, importance);
                            })}
                        </React.Fragment>
                    );
                } else {
                    let importance = option.importance;
                    let existing = selectedValues[option.value];
                    if (existing) {
                        importance = existing.importance;
                    }
                    return renderOption(option, index, importance);
                }
            })}

            <div className="ant-dropdown-menu-item-divider" />

            {/* Footer Done */}
            <div className="ant-dropdown-menu-item ant-dropdown-menu-item-group-title">
                <small className="text-bold flex-row flex-no-wrap flex ml-auto">
                    <span
                        className="ml-auto text-dark clickable px-2 text-uppercase"
                        onClick={onToggleEditing}
                    >
                        Done
                    </span>
                </small>
            </div>
        </div>
    );

    return (
        <div className="filter-button-dropdown" ref={containerRef}>
            <Dropdown
                open={isEditing}
                onOpenChange={(open) => !renderLabel && onToggleEditing()}
                dropdownRender={() => menuContent}
                trigger={['click']}
                disabled={isDisabled}
                placement={right ? 'bottomRight' : 'bottomLeft'}
                getPopupContainer={() => containerRef.current}
            >
                {renderLabel ? (
                    <div className={"filter-button dropdown-toggle " + (className || '')}>
                        {renderLabel}
                    </div>
                ) : (
                    <button
                        type="button"
                        disabled={isDisabled}
                        className={"filter-button dropdown-toggle " + (className || '')}
                    >
                        <span>{label} (
                            <span className={TB.mapClass("filter-button-label", {
                                active: (value || []).length > 0
                            })}>
                                {(value || []).length || "0"}
                            </span>
                            )
                        </span>
                    </button>
                )}
            </Dropdown>

            <div className="help-text">
                {formErrors && (
                    <p className="text-danger mb-0">
                        {formErrors}
                    </p>
                )}
            </div>
        </div>
    );
};

JobBoardFilter2.propTypes = {
    onChange: PropTypes.func,
    formErrors: PropTypes.any,
    onToggleEditing: PropTypes.func,
    options: PropTypes.any,
    isDisabled: PropTypes.any,
    searchEnabled: PropTypes.any,
    value: PropTypes.any,
    setImportanceLevel: PropTypes.any,
    setExperienceLevel: PropTypes.any,
    onChangeOptions: PropTypes.func,
    label: PropTypes.string,
    subLabel: PropTypes.string,
    className: PropTypes.string,
    isEditing: PropTypes.bool,
    right: PropTypes.bool,
    renderLabel: PropTypes.any,
};

export default JobBoardFilter2;
